// filepath: apps/web/src/features/admin/hmacKeys/RevokeConfirmDialog.tsx
//
// FR-UI-ADM-7 AC-12 -- typed-confirmation dialog for revoking an HMAC key.
//
// Admin must type the exact key_id to confirm (per FR-UI-CORE-6 pattern).
// The submit button is disabled until the typed input matches the key_id
// verbatim.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRevokeKey } from "./queries";

interface RevokeConfirmDialogProps {
  keyId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function RevokeConfirmDialog({
  keyId,
  onClose,
  onSuccess,
}: RevokeConfirmDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const revoke = useRevokeKey();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = typed === keyId && !revoke.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      await revoke.mutateAsync(keyId);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      data-testid="revoke-confirm-dialog"
      className="hmac-keys-dialog"
      role="dialog"
      aria-modal="true"
    >
      <h2>{t("admin.webhooks.keys.revoke_confirm_title")}</h2>
      <p>{t("admin.webhooks.keys.revoke_confirm_warning")}</p>
      <form onSubmit={handleSubmit}>
        <label htmlFor="revoke-confirm-input">
          {t("admin.webhooks.keys.key_id_label")}
        </label>
        <input
          id="revoke-confirm-input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={t("admin.webhooks.keys.revoke_confirm_placeholder")}
          data-testid="revoke-confirm-input"
        />
        {error && (
          <p data-testid="revoke-confirm-error" className="hmac-keys-error">
            {error}
          </p>
        )}
        <div className="hmac-keys-dialog__actions">
          <button
            type="button"
            onClick={onClose}
            data-testid="revoke-confirm-cancel"
          >
            {t("admin.webhooks.keys.cancel_button")}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="revoke-confirm-submit"
          >
            {t("admin.webhooks.keys.revoke_action")}
          </button>
        </div>
      </form>
    </div>
  );
}
